/**
 * Adapters for converting objects between API, Context, and Component formats
 */

import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../../services';
import { Team as ContextTeam, Organization as ContextOrg, Member as ContextMember } from '../../../context/OrganizationContext';

/**
 * Convert API team format to Context team format
 */
export function toContextTeam(apiTeam: any): ContextTeam {
  if (!apiTeam) return {} as ContextTeam;
  
  return {
    id: apiTeam._id || apiTeam.id || '',
    name: apiTeam.name || '',
    description: apiTeam.description,
    organizationId: apiTeam.organizationId || '',
    organizationName: apiTeam.organizationName,
    isPublic: apiTeam.isPublic,
    createdAt: apiTeam.createdAt || '',
    updatedAt: apiTeam.updatedAt || ''
  };
}

/**
 * Convert API organization format to Context organization format
 */
export function toContextOrg(apiOrg: any): ContextOrg {
  if (!apiOrg) return {} as ContextOrg;
  
  return {
    id: apiOrg._id || apiOrg.id || '',
    name: apiOrg.name || '',
    description: apiOrg.description,
    createdAt: apiOrg.createdAt || '',
    updatedAt: apiOrg.updatedAt || ''
  };
}

/**
 * Convert API member format to Context member format
 */
export function toContextMember(apiMember: any): ContextMember {
  if (!apiMember) return {} as ContextMember;
  
  // Generate a unique ID for the member
  // API v2 doesn't return an id field, so generate one from userId and roleId
  const userId = apiMember.userId || apiMember.user_id;
  const roleId = apiMember.roleId || apiMember.role_id;
  const memberId = apiMember._id || apiMember.id || 
    (userId && roleId ? `${userId}_${roleId}` : 
     userId ? `member_${userId}` : 
     `member_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  return {
    id: memberId,
    userId: userId || '',
    name: apiMember.name || '',
    email: apiMember.email || apiMember.maskedEmail || apiMember.masked_email || '',
    role: roleId || apiMember.role || apiMember.role_name || '',
    roleName: apiMember.roleName || apiMember.role_name, // Add roleName for display
    avatarUrl: apiMember.avatarUrl || apiMember.avatar,
    status: apiMember.status || (apiMember.isActive === true ? 'active' : apiMember.isActive === false ? 'inactive' : 'unknown'),
    createdAt: apiMember.createdAt || apiMember.joinedAt || apiMember.joined_at || '',
    updatedAt: apiMember.updatedAt || apiMember.updated_at || ''
  } as ContextMember & { roleName?: string };
}

/**
 * Convert an array of API members to Context members
 */
export function toContextMembers(apiMembers: any[]): ContextMember[] {
  if (!apiMembers || !Array.isArray(apiMembers)) return [];
  
  return apiMembers.map(toContextMember);
}

/**
 * Ensure property exists
 */
export function ensureProperty<T>(obj: T | null | undefined, key: keyof T, defaultValue: any): any {
  if (!obj) return defaultValue;
  return obj[key] !== undefined ? obj[key] : defaultValue;
}